import React, { useMemo, useState } from "react";
import { T, SERIF, GOLD_INK, statusColor } from "../theme/tokens.js";
import { Panel, Empty, Chip, Btn, Label, Notice } from "../components/ui/index.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import DeckPanel from "../components/DeckPanel.jsx";
import { useRegisters } from "../lib/useRegister.js";
import { buildReport, monthKey, recentMonths } from "../lib/report.js";
import { fmtMoney, fmtDate } from "../lib/format.js";
import { term, termPlural, termLower, termPluralLower } from "../lib/terms.js";

/* The monthly report, assembled rather than written.
 *
 * Six of the deck's sections are arithmetic and are built here from the rows,
 * as at the end of the month rather than as at today: running August's report
 * in October has to produce August's numbers or last month's deck stops
 * matching itself the day after it is sent.
 *
 * The rest of the deck is writing that arrives in vendor reports and meeting
 * minutes through the month. Those sections are shown here as the empty boxes
 * they are, named, in the right order, so the shape of the deck is honest
 * about what is filled in and what is not.
 */

const BOX = {
  green: { fill: "#3E9B62", ink: "#fff", label: "All complete" },
  amber: { fill: "#C0761A", ink: "#fff", label: "Open, nothing late" },
  red: { fill: "#8F2B25", ink: "#fff", label: "Something late" },
  none: { fill: "transparent", ink: T.soft, label: "None" },
};

function Section({ n, title, children, right, note }) {
  return (
    <Panel
      title={`${n}. ${title}`}
      right={right}
    >
      {note && (
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>{note}</div>
      )}
      {children}
    </Panel>
  );
}

/* A line as it reads on a slide, with where it came from underneath it.
   The source note is small and grey rather than absent, because the first
   question anybody asks about a sentence in a client report is where it came
   from, and the answer should not require opening another page. */
function Line({ entry }) {
  return (
    <li style={{ marginBottom: 7, lineHeight: 1.55 }}>
      <span style={{ color: T.ink }}>{entry.title}</span>
      {entry.source_note && (
        <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{entry.source_note}</div>
      )}
    </li>
  );
}

const Quadrant = ({ title, items, colour }) => (
  <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, padding: "9px 11px" }}>
    <div style={{ marginBottom: 6 }}><Label color={colour}>{title}</Label></div>
    {items.length === 0
      ? <div style={{ fontSize: 12, color: T.soft, fontStyle: "italic" }}>Nothing recorded</div>
      : (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5 }}>
          {items.map((e) => <Line key={e.id} entry={e} />)}
        </ul>
      )}
  </div>
);

/* What to say when a written section has nothing in it. Naming the month and
   the way in matters: "nothing here" sends somebody looking for a bug, and
   "nothing was recorded for July" sends them to the right page. */
const NothingYet = ({ label, what }) => (
  <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7 }}>
    {what}
    <br /><br />
    Nothing is recorded for {label}. This comes from the communications log:
    add the vendor reports and minutes on Communications with {label} selected,
    read them, and keep the lines you agree with. They appear here.
  </div>
);

const cell = { padding: "6px 9px", fontSize: 12.5, borderBottom: `1px solid ${T.line}` };
const head = { ...cell, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
               textTransform: "uppercase", color: T.navy, borderBottom: `2px solid ${T.navy}`,
               textAlign: "left", whiteSpace: "nowrap" };

export default function Reports({ contract }) {
  const { data } = useRegisters(
    ["deliverables", "risks", "issues", "invoices", "vendors", "workstream_status", "comms_entries"],
    contract?.id,
  );
  return <ReportsView contract={contract} data={data} deck />;
}

/* The view, separated from the fetch so the report can be rendered against
   known numbers and looked at. A report nobody has looked at is a report
   nobody should send. */
export function ReportsView({ contract, data, initialMonth, deck = false }) {
  const [month, setMonth] = useState(initialMonth || monthKey());
  const [drill, setDrill] = useState(null);
  const open = (kind, title, rows) => setDrill(rows?.length ? { kind, title, rows } : null);

  const report = useMemo(
    () => (data ? buildReport({ contract, data, month }) : null),
    [contract, data, month]);

  if (!report) return <Empty text="Loading..." />;

  const months = recentMonths(monthKey(), 12).reverse();
  const t = report.tracker;
  const nar = report.narrative;
  /* WAPA calls these workstreams and VIDE calls them stages. Same column,
     different word, and the deck has to use the client's. */
  const one = term(contract);
  const many = termPluralLower(contract);

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <DrillPanel drill={drill} onClose={() => setDrill(null)} />

      <Panel
        title={`Monthly status report · ${report.label}`}
        right={
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            style={{ padding: "5px 9px", border: `1px solid ${T.line}`, borderRadius: 4,
                     fontSize: 13, background: "#fff", color: T.ink }}>
            {months.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        }
      >
        <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7 }}>
          Every number here is as at <strong>{fmtDate(report.asOf)}</strong>, the end of the
          month being reported, not as at today. So this report reads the same
          in December as it did when it was sent, which is the whole point of a
          monthly.
          <br /><br />
          The sections are in the order your deck puts them. The ones marked
          written are the ones a person writes.
        </div>
      </Panel>

      {/* The deck reads the database for its template and the schedule, so the
          stand-alone view used for looking at known numbers goes without it. */}
      {deck && <DeckPanel key={contract?.id} contract={contract} report={report} data={data} />}

      <Section n={1} title="Executive summary"
        right={<Chip color={GOLD_INK}>{nar.total} line{nar.total === 1 ? "" : "s"}</Chip>}
        note={`One box per ${termLower(contract)}, from what the month's documents said. Every line here was read off a vendor report or a set of minutes and kept by a person.`}>
        {nar.byWorkstream.length === 0
          ? <NothingYet label={report.label}
              what={`One box per ${termLower(contract)}, each summarising where it stands.`} />
          : (
            <div style={{ display: "grid", gap: 10,
                          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              {nar.byWorkstream.map((g) => (
                <div key={g.workstream} style={{
                  border: `1px solid ${T.line}`, borderTop: `3px solid ${T.navy}`,
                  borderRadius: 4, padding: "10px 12px",
                }}>
                  <div style={{ fontFamily: SERIF, fontSize: 14, color: T.navy,
                                fontWeight: 700, marginBottom: 6 }}>
                    {g.workstream}
                  </div>
                  {g.accomplishments.length === 0
                    ? <div style={{ fontSize: 12, color: T.soft, fontStyle: "italic" }}>
                        Nothing completed recorded this month
                      </div>
                    : (
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5 }}>
                        {g.accomplishments.map((e) => <Line key={e.id} entry={e} />)}
                      </ul>
                    )}
                  {(g.risks.length > 0) && (
                    <div style={{ fontSize: 11.5, color: "#8F2B25", marginTop: 6 }}>
                      {g.risks.length} risk{g.risks.length === 1 ? "" : "s"} raised
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </Section>

      <Section n={2} title="Top 5 outcomes"
        right={<Chip color={GOLD_INK}>{nar.outcomes.length} of {nar.accomplishments.length}</Chip>}
        note="The first five accomplishments kept for this month, in the order they were kept. Nothing is ranked for you: reorder them in Communications if the wrong five are leading.">
        {nar.outcomes.length === 0
          ? <NothingYet label={report.label}
              what="Five numbered outcomes, each a headline and a supporting sentence." />
          : (
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5 }}>
              {nar.outcomes.map((e) => <Line key={e.id} entry={e} />)}
            </ol>
          )}
      </Section>

      <Section n={3} title="Overall project status"
        note={`Your team's own words, from the ${termLower(contract)} status register, for ${report.label}. Each month has its own rows, so writing this month's status does not rewrite what last month's report said.`}
        right={<Chip color={T.steel}>{report.workstreams.length} {many}</Chip>}>
        {report.workstreams.length === 0
          ? <Empty text={`No ${termLower(contract)} statuses recorded for ${report.label}. Add them on Contracts, ${one} status, with the month set to ${month}, and they appear here.`} />
          : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={head}>{one}</th><th style={head}>Status</th><th style={head}>Key comments</th>
              </tr></thead>
              <tbody>
                {report.workstreams.map((w) => (
                  <tr key={w.title}>
                    <td style={{ ...cell, fontWeight: 700, color: T.navy }}>{w.title}</td>
                    <td style={cell}><Chip color={statusColor(w.status)}>{w.status || "Not set"}</Chip></td>
                    <td style={{ ...cell, color: T.ink }}>
                      {w.comment || ""}
                      {w.undated && (
                        <div style={{ fontSize: 11.5, color: GOLD_INK, marginTop: 3 }}>
                          No month set on this one, so it will appear in every month's
                          report until one is.
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>

      <Section n={4} title={`${one} slides`}
        right={<Chip color={T.steel}>{nar.byWorkstream.length} slide{nar.byWorkstream.length === 1 ? "" : "s"}</Chip>}
        note={`One slide per ${termLower(contract)}, in the deck's four quadrants. A ${termLower(contract)} with nothing recorded this month gets no slide rather than an empty one.`}>
        {nar.byWorkstream.length === 0
          ? <NothingYet label={report.label}
              what={`One slide per ${termLower(contract)}, each in four quadrants: Key Accomplishments, Upcoming Tasks, Key Risks and Issues, Consulting Insight.`} />
          : (
            <div style={{ display: "grid", gap: 14 }}>
              {nar.byWorkstream.map((g) => (
                <div key={g.workstream}>
                  <div style={{ fontFamily: SERIF, fontSize: 15, color: T.navy,
                                fontWeight: 700, marginBottom: 8 }}>
                    {g.workstream}
                  </div>
                  <div style={{ display: "grid", gap: 8,
                                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                    <Quadrant title="Key accomplishments" colour="#3E6B4F" items={g.accomplishments} />
                    <Quadrant title="Upcoming tasks" colour={T.steel} items={g.upcoming} />
                    <Quadrant title="Key risks and issues" colour="#8F2B25"
                      items={[...g.risks, ...g.decisions]} />
                    <Quadrant title="Consulting insight" colour={GOLD_INK} items={g.insights} />
                  </div>
                </div>
              ))}
              {nar.unassigned.length > 0 && (
                <div>
                  <div style={{ marginBottom: 6 }}>
                    <Label color={GOLD_INK}>No {termLower(contract)} set · {nar.unassigned.length}</Label>
                  </div>
                  <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 6, lineHeight: 1.6 }}>
                    These lines were kept but never assigned to a {termLower(contract)},
                    so they belong to no slide. Set one on each in Communications and
                    they move into place.
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                    {nar.unassigned.map((e) => <Line key={e.id} entry={e} />)}
                  </ul>
                </div>
              )}
            </div>
          )}
      </Section>

      <Section n={5} title="Contract deliverables tracker"
        right={<Chip color={T.steel}>{t.program.complete} of {t.program.total} complete</Chip>}
        note={`Complete as at ${fmtDate(report.asOf)}, read off each deliverable's completion date, so a different month gives different numbers. Green means everything in the box was complete by then. Amber means open and nothing had missed its date. Red means something was past its date. A blank means that vendor has nothing in that stage.`}>
        {t.undated > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Notice>
              {t.undated} deliverable{t.undated === 1 ? " is" : "s are"} marked complete with no
              completion date, so {t.undated === 1 ? "it counts" : "they count"} in every month and
              this table will look similar month to month until
              {" "}{t.undated === 1 ? "it is" : "they are"} dated. Fill in the completed date on
              Contracts, Deliverables.
            </Notice>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={head}>Vendor</th>
              {t.stages.map((s) => <th key={s} style={head}>{s}</th>)}
              <th style={head}>Total complete</th>
            </tr></thead>
            <tbody>
              {[...t.rows, ...t.clientRows].map((r) => (
                <tr key={r.vendor}>
                  <td style={{ ...cell, fontWeight: 700, color: T.navy, whiteSpace: "nowrap" }}>
                    {r.vendor}{r.client ? " (client approvals)" : ""}
                  </td>
                  {r.cells.map((c) => (
                    <td key={c.stage} style={{ ...cell, textAlign: "center", padding: 4 }}>
                      {c.state === "none" ? <span style={{ color: T.soft }}>&ndash;</span> : (
                        <button onClick={() => open("deliverables", `${r.vendor} · ${c.stage}`, c.items)}
                          title={BOX[c.state].label}
                          style={{ background: BOX[c.state].fill, color: BOX[c.state].ink,
                                   border: "none", borderRadius: 3, padding: "4px 9px",
                                   fontSize: 12, fontWeight: 700, cursor: "pointer", minWidth: 54 }}>
                          {c.complete} / {c.total}
                        </button>
                      )}
                    </td>
                  ))}
                  <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
                    {r.complete} / {r.total}
                  </td>
                </tr>
              ))}
              <tr style={{ background: T.chip }}>
                <td style={{ ...cell, fontWeight: 700 }}>Vendor subtotal</td>
                {t.vendorSubtotal.cells.map((c) => (
                  <td key={c.stage} style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
                    {c.complete} / {c.total}
                  </td>
                ))}
                <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
                  {t.vendorSubtotal.complete} / {t.vendorSubtotal.total}
                </td>
              </tr>
              <tr style={{ background: T.chip }}>
                <td style={{ ...cell, fontWeight: 700 }}>Total deliverables</td>
                {t.program.cells.map((c) => (
                  <td key={c.stage} style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
                    {c.complete} / {c.total}
                  </td>
                ))}
                <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
                  {t.program.complete} / {t.program.total}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section n={6} title="Overdue deliverables"
        right={<Chip color={report.overdue.length ? "#8F2B25" : T.ok}>
          {report.overdue.reduce((s, r) => s + r.count, 0)} overdue
        </Chip>}
        note="What is late at month end, by vendor and stage. The reason each one is late is writing, and stays yours.">
        {report.overdue.length === 0
          ? <Empty text="Nothing was overdue at the end of this month." />
          : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={head}>Vendor</th><th style={head}>Late</th><th style={head}>What is late</th>
              </tr></thead>
              <tbody>
                {report.overdue.map((r) => (
                  <tr key={r.vendor}>
                    <td style={{ ...cell, fontWeight: 700, color: T.navy, whiteSpace: "nowrap" }}>{r.vendor}</td>
                    <td style={cell}>
                      <button onClick={() => open("deliverables", `${r.vendor} · overdue`, r.items)}
                        style={{ background: "none", border: "none", cursor: "pointer",
                                 color: "#8F2B25", fontWeight: 700, fontSize: 13, padding: 0 }}>
                        {r.count}
                      </button>
                      {r.contractCount > 0 && (
                        <span style={{ fontSize: 11.5, color: T.soft }}> · {r.contractCount} CD</span>
                      )}
                    </td>
                    <td style={{ ...cell, lineHeight: 1.6 }}>
                      {r.stages.map((s) => (
                        <div key={s.stage}>
                          <strong>{s.stage} ({s.items.length}):</strong>{" "}
                          {s.items.map((d) => d.title).join(", ")}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>

      <Section n={7} title="Overall project key risks and issues"
        right={<Chip color={T.steel}>{report.risks.length} shown</Chip>}
        note="The register as it stands today, not as it stood at month end. A risk row records what is true now and carries no history, so this section does not change when you change the month. Worst first, and the mitigation is whatever the register says.">
        {report.risks.length === 0
          ? <Empty text="No open risks on the register." />
          : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={head}>Key risk</th><th style={head}>Status</th><th style={head}>Score</th>
                <th style={head}>Owner</th><th style={head}>Mitigation plan</th>
              </tr></thead>
              <tbody>
                {report.risks.map(({ risk, score, high }) => (
                  <tr key={risk.id ?? risk.title}>
                    <td style={{ ...cell, fontWeight: 700, color: T.navy }}>{risk.title}</td>
                    <td style={cell}><Chip color={statusColor(risk.status)}>{risk.status}</Chip></td>
                    <td style={cell}>
                      {score === null
                        ? <span style={{ color: T.soft }}>{risk.rating || "Not scored"}</span>
                        : <Chip color={high ? "#8F2B25" : T.soft}>{score}</Chip>}
                    </td>
                    <td style={cell}>{risk.owner || ""}</td>
                    <td style={{ ...cell, lineHeight: 1.6 }}>{risk.mitigation || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>

      <Section n={8} title="Outstanding invoices, beyond payment terms"
        right={<Chip color={report.invoices.beyondTerms.count ? "#8F2B25" : T.ok}>
          {report.invoices.beyondTerms.count} · {fmtMoney(report.invoices.beyondTerms.dollars)}
        </Chip>}
        note="Against each vendor's own terms rather than a flat sixty days, which is why your August deck stopped saying over 60. AWG is on 45.">
        {report.invoices.beyondTerms.count === 0
          ? <Empty text="No invoices were beyond terms at the end of this month." />
          : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={head}>Vendor</th><th style={head}>Invoice</th><th style={head}>Submitted</th>
                  <th style={head}>Amount</th><th style={head}>Aging</th><th style={head}>Description</th>
                </tr></thead>
                <tbody>
                  {report.invoices.beyondTerms.items.map(({ invoice, aging }) => (
                    <tr key={invoice.id ?? invoice.title}>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>{invoice.vendor}</td>
                      <td style={{ ...cell, fontWeight: 700, color: T.navy, whiteSpace: "nowrap" }}>{invoice.title}</td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>{fmtDate(invoice.submitted)}</td>
                      <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>{fmtMoney(invoice.amount)}</td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>
                        <Chip color="#8F2B25">{aging.pastDays + (aging.termsDays ?? 0)} days</Chip>
                      </td>
                      <td style={cell}>{invoice.description || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Section>

      <Section n={9} title="Outstanding invoices, month over month"
        note="Rebuilt from the invoices themselves rather than from anything saved, so a month nobody ran a report in still has a figure.">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={head}>Measure</th>
              {report.trend.map((t2) => <th key={t2.month} style={head}>{t2.label}</th>)}
            </tr></thead>
            <tbody>
              <tr>
                <td style={{ ...cell, fontWeight: 700 }}>Invoices beyond terms</td>
                {report.trend.map((t2) => (
                  <td key={t2.month} style={{ ...cell, textAlign: "center" }}>{t2.count}</td>
                ))}
              </tr>
              <tr>
                <td style={{ ...cell, fontWeight: 700 }}>Dollars beyond terms</td>
                {report.trend.map((t2) => (
                  <td key={t2.month} style={{ ...cell, textAlign: "center", whiteSpace: "nowrap" }}>
                    {fmtMoney(t2.dollars)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Notice>
        Still to come in the deck: the written slides (the executive summary,
        outcomes and the four quadrant slides) drafted from the communications
        log, and the six month invoice trend, which needs each month frozen
        when its report is issued.
      </Notice>
    </div>
  );
}
